// src/components/StudentClassLookup.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import classRoomsData from '../data/classRooms.json';
import './StudentClassLookup.css';

// Dựng bảng tra cứu nhanh O(1) theo Mã Lớp (classId)
const classRoomsMap = {};
classRoomsData.forEach((item) => {
  classRoomsMap[item.classId] = item;
});

// Hàm chuẩn hóa chuỗi tiếng Việt tìm kiếm không dấu
const removeVietnameseTones = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();
};

let cachedCombinedStudents = null; // Cache bộ nhớ tạm tránh đọc lặp lại Firestore

const StudentClassLookup = () => {
  const [keyword, setKeyword] = useState('');
  const [students, setStudents] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const wrapperRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      if (cachedCombinedStudents) {
        setStudents(cachedCombinedStudents);
        return;
      }

      setIsLoadingStudents(true);
      try {
        // Nạp song song cả 2 collection: Hồ sơ học sinh và Phân lớp niên khóa 2026-2027
        const [studentsSnap, enrollmentsSnap] = await Promise.all([
          getDocs(collection(firestore, 'students')),
          getDocs(collection(firestore, 'academic_years/2026-2027/enrollments'))
        ]);

        // Tạo map phân lớp theo studentId (hoặc document id)
        const enrollmentMap = {};
        enrollmentsSnap.docs.forEach((doc) => {
          const data = doc.data();
          const sId = data.studentId || doc.id;
          enrollmentMap[sId] = data;
        });

        // Kết hợp dữ liệu học sinh với thông tin phân lớp và phòng học
        const combinedList = studentsSnap.docs.map((doc) => {
          const sData = doc.data();
          const sId = doc.id;
          const enrollment = enrollmentMap[sId];
          const classId = enrollment?.classId || sData.classId || '';

          const roomInfo = classRoomsMap[classId] || {
            className: sData.className || 'Chưa xếp lớp',
            room: 'Chưa phân phòng'
          };

          const holyName = sData.holyName || '';
          const fullName = sData.fullName || '';
          const searchKey = removeVietnameseTones(`${holyName} ${fullName}`);

          return {
            id: sId,
            holyName,
            fullName,
            classId,
            className: roomInfo.className,
            room: roomInfo.room,
            searchKey
          };
        });

        cachedCombinedStudents = combinedList;
        setStudents(combinedList);
      } catch (err) {
        console.error('Lỗi khi tải dữ liệu tra cứu lớp:', err);
      } finally {
        setIsLoadingStudents(false);
      }
    };

    loadData();
  }, []);

  // Lọc kết quả tìm kiếm theo ký tự nhập
  useEffect(() => {
    const term = removeVietnameseTones(keyword);
    if (!term || term.length < 2) {
      setSuggestions([]);
      return;
    }

    const filtered = students
      .filter((st) => st.searchKey.includes(term))
      .slice(0, 8);

    setSuggestions(filtered);
  }, [keyword, students]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectStudent = (st) => {
    setSelectedStudent(st);
    setKeyword('');
    setSuggestions([]);
    setIsFocused(false);
  };

  return (
    <div className="scl-container" ref={wrapperRef}>
      <div className="scl-badge-header">
        <span className="scl-badge-icon">🏫</span>
        <h4>Tra Cứu Lớp & Phòng Học</h4>
      </div>
      <p className="scl-subtext">Nhập họ tên thiếu nhi để xem số phòng học</p>

      <div className="scl-input-wrapper">
        <input
          type="text"
          className="scl-input"
          placeholder={isLoadingStudents ? 'Đang chuẩn bị dữ liệu...' : 'Nhập họ và tên thiếu nhi...'}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onFocus={() => setIsFocused(true)}
          disabled={isLoadingStudents}
        />
        {keyword && (
          <button className="scl-clear-btn" onClick={() => setKeyword('')}>
            ✕
          </button>
        )}
      </div>

      {/* Danh sách gợi ý */}
      {isFocused && suggestions.length > 0 && (
        <div className="scl-dropdown">
          {suggestions.map((st) => (
            <div
              key={st.id}
              className="scl-dropdown-item"
              onClick={() => handleSelectStudent(st)}
            >
              <div className="scl-item-name">
                <strong>{st.holyName ? `${st.holyName} ` : ''}{st.fullName}</strong>
              </div>
              <div className="scl-item-meta">
                <span className="scl-class-tag">{st.className}</span>
                <span className="scl-room-hint">📍 {st.room}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {isFocused && keyword.trim().length >= 2 && suggestions.length === 0 && !isLoadingStudents && (
        <div className="scl-dropdown scl-empty">
          Không tìm thấy thiếu nhi khớp với từ khóa "{keyword}"
        </div>
      )}

      {/* Modal hiển thị chi tiết */}
      {selectedStudent && (
        <div className="scl-modal-overlay" onClick={() => setSelectedStudent(null)}>
          <div className="scl-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="scl-modal-header">
              <div className="scl-modal-icon">⛪</div>
              <h3>Thông Tin Lớp & Phòng Học</h3>
              <button className="scl-modal-close" onClick={() => setSelectedStudent(null)}>✕</button>
            </div>

            <div className="scl-modal-body">
              <div className="scl-info-row">
                <span className="scl-label">Tên Thánh:</span>
                <span className="scl-value highlight">{selectedStudent.holyName || '—'}</span>
              </div>

              <div className="scl-info-row">
                <span className="scl-label">Họ và Tên:</span>
                <span className="scl-value bold">{selectedStudent.fullName}</span>
              </div>

              <div className="scl-info-row">
                <span className="scl-label">Tên Lớp:</span>
                <span className="scl-value class-badge">{selectedStudent.className}</span>
              </div>

              <div className="scl-room-highlight-box">
                <div className="scl-room-label">SỐ PHÒNG HỌC</div>
                <div className="scl-room-value">🚪 {selectedStudent.room}</div>
              </div>
            </div>

            <div className="scl-modal-footer">
              <button className="scl-modal-btn" onClick={() => setSelectedStudent(null)}>
                Đã hiểu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentClassLookup;